import { Loading } from 'components/loading/Loading';
import ToggleSwitch from 'components/ToggleSwitch';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { fetchCronJobs, toggleJobActiveStatus } from 'services/api/cronJobs';
import CustomLink from 'components/TableLink';
import { DATE_FORMATS, formatDate } from 'utils';
import { showError, showSuccess } from 'utils/notifications';
import styles from './index.module.scss';
import DataTableFilter from 'components/DataTableFIlter';

const CronJobs = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [activeLoading, setActiveLoading] = useState<string>('');
  const [cronJobs, setCronJobs] = useState<any>([]);
  const [filterText, setFilterText] = useState<string>('');

  const fetchCronJobsFn = async () => {
    try {
      const response = await fetchCronJobs();
      setCronJobs(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCronJobsFn();
  }, []);

  const toggleCronJobStatus = async (jobDetails: Record<string, any>) => {
    const {
      objectId: id,
      name,
      active,
    } = jobDetails;
    setActiveLoading(id);
    try {
      await toggleJobActiveStatus(id);
      await fetchCronJobsFn();
      showSuccess(`${name} marked ${active ? 'INACTIVE' : 'ACTIVE'} successfully`);
    } catch (e) {
      showError(e);
    } finally {
      setActiveLoading('');
    }
  };

  const columns = useMemo(() => [
    {
      name: 'Job ID',
      selector: 'objectId',
      sortable: false,
      width: '120px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          linkTo={`/cron-jobs/${row.objectId}`}
          text={row.objectId}
          openInNewTab={false}
        />
      ),
    },
    {
      name: 'Created At',
      selector: 'createdAt',
      sortable: false,
      width: '175px',
      format: row => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
    },
    {
      name: 'Name',
      selector: 'name',
      sortable: false,
      width: '250px',
    },
    {
      name: 'Job Server Instance',
      selector: 'jobServerInstance',
      width: 'auto',
      sortable: false,
    },
    {
      name: 'CRON Expression',
      selector: 'cronExpressionStr',
      sortable: false,
      width: '300px',
      format: row => row.cronExpressionStr ? row.cronExpressionStr : '-',
    },
    {
      name: 'Last Run Time',
      selector: 'lastRunTime',
      sortable: false,
      width: '150px',
      format: row => formatDate(row.lastRunTime?.iso, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
    },
    {
      name: 'Last Error Time',
      selector: 'lastErrorTime',
      sortable: false,
      format: row => row.lastErrorTime ? formatDate(row.lastErrorTime?.iso, DATE_FORMATS.MM_DD_YYYY_hh_mm_A) : '-',
    },
    {
      name: 'Active',
      selector: 'active',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <ToggleSwitch
          switchLabel={''}
          checked={row.active}
          disabled={activeLoading === row.objectId}
          onChange={() => toggleCronJobStatus(row)}
        />
      ),
    },
  ], [activeLoading, loading]);

  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>CRON Jobs</div>
    </div>
  );

  const getDataTable = () => {
    if (loading) return <Loading />;
    if (cronJobs.length) {
      const JobNameFilter = <DataTableFilter filterText={filterText} setFilterText={setFilterText} placeholder={'Filter by Job Name'} />;
      const filteredData = cronJobs.filter(job => job.name && job.name.toLowerCase().includes(filterText.toLowerCase()));

      return (
        <DataTable
          keyField={'objectId'}
          columns={columns}
          data={filteredData}
          actions={JobNameFilter}
          highlightOnHover={true}
          customStyles={tableStyles}
        />
      );
    }
    return <div />;
  };
  return (
    <div>
      {getHeader()}
      {getDataTable()}
    </div>
  );
};

export default CronJobs;